/**
 * The single Brazilian date/time formatter for everything a person reads (screen, files, e-mail, alert messages).
 * Instants: DD/MM/AAAA HH:mm:ss, 24 h, in the unit's IANA timezone through Intl, so historical daylight saving
 * time is applied; never a fixed offset. Operational dates (DATE, 'YYYY-MM-DD') are labels: they are rearranged,
 * never converted to an instant, so no timezone can shift them. The JSON API keeps ISO 8601 (contracts/common.ts).
 * Every formatter returns null for an absent or invalid value; the caller decides what to show ('—', '').
 */
import { DEFAULT_TIMEZONE } from './dates.ts';

export class DateTimeError extends Error {}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_MONTH = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/;
// An instant must carry its offset: a wall-clock string without one would be read in the runtime's timezone.
const ISO_INSTANT =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?(?:Z|[+-](\d{2}):(\d{2}))$/;

function isRealDate(y: number, m: number, d: number): boolean {
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

/** Intl.DateTimeFormat is expensive to build; one per timezone. h23: midnight is 00, never 24. */
function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    formatters.set(timeZone, f);
  }
  return f;
}

interface WallClock {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
}

/** Local wall clock of an instant, read from parts so the output never depends on locale punctuation. */
function wallClock(instant: Date, timeZone: string): WallClock {
  const parts = partsFormatter(timeZone).formatToParts(instant);
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? '';
  return {
    year: get('year').padStart(4, '0'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

function toInstant(value: Date | string): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const m = ISO_INSTANT.exec(value);
  // V8 rolls 2026-02-30 over to March and accepts 24:00; both are refused here instead of shown as another day.
  if (
    !m ||
    !isRealDate(Number(m[1]), Number(m[2]), Number(m[3])) ||
    Number(m[4]) > 23 ||
    Number(m[5]) > 59 ||
    Number(m[6] ?? 0) > 59 ||
    Number(m[7] ?? 0) > 14 ||
    Number(m[8] ?? 0) > 59
  ) {
    return null;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * The unit's timezone (parking_units.timezone). Absent → America/Sao_Paulo, the column default. An unknown name
 * throws: falling back silently would print every time of that unit one or more hours off.
 */
export function resolveUnitTimezone(timeZone: string | null | undefined): string {
  if (timeZone === null || timeZone === undefined || timeZone.trim() === '') return DEFAULT_TIMEZONE;
  try {
    partsFormatter(timeZone);
  } catch {
    throw new DateTimeError(`Fuso horário desconhecido: ${timeZone}`);
  }
  return timeZone;
}

/** 'YYYY-MM-DD' → 'DD/MM/AAAA'. Only a real calendar date is accepted; an instant is not a date. */
export function formatDateBr(isoDate: string | null | undefined): string | null {
  if (!isoDate) return null;
  const m = ISO_DATE.exec(isoDate);
  if (!m || !isRealDate(Number(m[1]), Number(m[2]), Number(m[3]))) return null;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** Competence month: 'YYYY-MM' or 'YYYY-MM-DD' → 'MM/AAAA'. */
export function formatMonthBr(isoMonth: string | null | undefined): string | null {
  if (!isoMonth) return null;
  const m = ISO_MONTH.exec(isoMonth);
  if (!m) return null;
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  if (m[3] !== undefined && !isRealDate(Number(m[1]), month, Number(m[3]))) return null;
  return `${m[2]}/${m[1]}`;
}

/** Instant (Date, or ISO 8601 with Z or an offset) → 'DD/MM/AAAA HH:mm:ss' in the given timezone. */
export function formatDateTimeBr(
  instant: Date | string | null | undefined,
  timeZone: string = DEFAULT_TIMEZONE,
): string | null {
  if (instant === null || instant === undefined || instant === '') return null;
  const d = toInstant(instant);
  if (!d) return null;
  const w = wallClock(d, resolveUnitTimezone(timeZone));
  return `${w.day}/${w.month}/${w.year} ${w.hour}:${w.minute}:${w.second}`;
}

/** The calendar date ('YYYY-MM-DD') an instant falls on in the given timezone. */
export function localDateOf(instant: Date, timeZone: string = DEFAULT_TIMEZONE): string {
  const w = wallClock(instant, resolveUnitTimezone(timeZone));
  return `${w.year}-${w.month}-${w.day}`;
}

/**
 * First instant of an operational date in the given timezone. Usually local 00:00, but Brazil started daylight
 * saving time at midnight (e.g. 2018-11-04 went from 23:59:59 straight to 01:00), so the day can begin at 01:00.
 * Binary search by minute between the earliest and latest possible offsets: correct for any transition rule.
 */
export function startOfLocalDay(isoDate: string, timeZone: string = DEFAULT_TIMEZONE): Date {
  const m = ISO_DATE.exec(isoDate);
  if (!m || !isRealDate(Number(m[1]), Number(m[2]), Number(m[3]))) {
    throw new DateTimeError(`Data inválida: ${isoDate}`);
  }
  const tz = resolveUnitTimezone(timeZone);
  const utcMidnight = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  // UTC offsets range from -12:00 to +14:00: the day starts somewhere in this window.
  let lo = (utcMidnight - 14 * 3_600_000) / 60_000;
  let hi = (utcMidnight + 12 * 3_600_000) / 60_000;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (localDateOf(new Date(mid * 60_000), tz) >= isoDate) hi = mid;
    else lo = mid + 1;
  }
  return new Date(lo * 60_000);
}
